// The audit log. Every long-running action in the cluster lands here.
//
// Filtering, sorting and pagination are all server-side and all mirrored
// into the URL, so "show me every failed publish-chromium, newest last" is
// a link an operator can paste into an incident channel.

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { Button } from "@veil/ui/components/button";
import {
	DataTable,
	DataTableColumnHeader,
	DataTableToolbar,
	getSortingStateParser,
	type Option,
	useDataTable,
} from "@veil/ui/data-table";
import { formatRelative } from "@veil/ui/lib/format";
import Activity from "lucide-react/dist/esm/icons/activity";
import {
	parseAsArrayOf,
	parseAsInteger,
	parseAsString,
	useQueryState,
} from "nuqs";
import { useMemo } from "react";
import { Link } from "wouter";
import { EmptyState, QueryError } from "@/components/query-state";
import { StatusBadge } from "@/components/status-badge";
import { api, unwrap } from "@/lib/api";
import { describeKind } from "@/lib/operations/use-active-operations";

const DEFAULT_PAGE_SIZE = 25;
const LIST_REFETCH_MS = 5_000;
/** Kinds only grow when a new pipeline ships; no need to poll for them. */
const KINDS_STALE_MS = 5 * 60_000;

interface OperationRow {
	id: string;
	kind: string;
	status: string;
	startedAt: string;
	finishedAt: string | null;
	exitCode: number | null;
	user: { username: string } | null;
}

interface OperationsResponse {
	success: boolean;
	data: OperationRow[];
	pagination: {
		page: number;
		pageSize: number;
		total: number;
		pageCount: number;
	};
}

interface KindsResponse {
	kinds: Array<{ kind: string; count: number }>;
}

const STATUS_OPTIONS: Option[] = [
	{ label: "Running", value: "running" },
	{ label: "Succeeded", value: "succeeded" },
	{ label: "Failed", value: "failed" },
	{ label: "Cancelled", value: "cancelled" },
];

// Only these two are sortable server-side; the parser rejects anything else
// so a hand-edited URL can't ask for an ordering the backend won't honour.
const SORTABLE_COLUMN_IDS = new Set(["startedAt", "finishedAt"]);
const DEFAULT_SORTING = [{ id: "startedAt" as const, desc: true }];

export function Operations() {
	// These mirror the keys useDataTable owns below. nuqs shares state across
	// hooks reading the same key, so both views of the URL stay identical —
	// this pair exists only because the request has to be built before the
	// table (which needs pageCount from the response).
	const [page] = useQueryState("page", parseAsInteger.withDefault(1));
	const [perPage] = useQueryState(
		"perPage",
		parseAsInteger.withDefault(DEFAULT_PAGE_SIZE),
	);
	const [sorting] = useQueryState(
		"sort",
		getSortingStateParser<OperationRow>(SORTABLE_COLUMN_IDS).withDefault(
			DEFAULT_SORTING,
		),
	);
	const [kindFilter] = useQueryState(
		"kind",
		parseAsArrayOf(parseAsString, ","),
	);
	const [statusFilter] = useQueryState(
		"status",
		parseAsArrayOf(parseAsString, ","),
	);

	const sort = sorting[0]?.id ?? "startedAt";
	const order = sorting[0]?.desc === false ? "asc" : "desc";
	const kind = kindFilter?.join(",");
	const status = statusFilter?.join(",");

	const { data, isLoading, isError, error, isFetching, refetch } = useQuery({
		queryKey: ["operations", page, perPage, sort, order, kind, status],
		queryFn: () =>
			unwrap(
				api.api.operations.get({
					query: {
						page,
						pageSize: perPage,
						sort,
						order,
						...(kind ? { kind } : {}),
						...(status ? { status } : {}),
					},
				}),
			) as Promise<OperationsResponse>,
		refetchInterval: LIST_REFETCH_MS,
		// Keep the current rows on screen while the next page loads so the
		// 5s poll and page flips never flash the skeleton state.
		placeholderData: keepPreviousData,
	});

	const kindsQuery = useQuery({
		queryKey: ["operations", "kinds"],
		queryFn: async () =>
			(await unwrap(api.api.operations.kinds.get())) as KindsResponse,
		staleTime: KINDS_STALE_MS,
	});

	const kindOptions = useMemo<Option[]>(
		() =>
			(kindsQuery.data?.kinds ?? []).map((entry) => ({
				label: describeKind(entry.kind),
				value: entry.kind,
				count: entry.count,
			})),
		[kindsQuery.data],
	);

	const columns = useMemo<ColumnDef<OperationRow>[]>(
		() => [
			{
				id: "kind",
				accessorKey: "kind",
				header: "Kind",
				cell: ({ row }) => (
					// Stretched link: the visible target is the kind, but the
					// pseudo-element covers the whole (relative) row. Still a real
					// anchor, so Tab, Enter and middle-click all behave.
					<Link
						href={`/operations/${row.original.id}`}
						className="font-mono text-xs after:absolute after:inset-0 hover:underline focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
					>
						{row.original.kind}
					</Link>
				),
				enableSorting: false,
				enableColumnFilter: true,
				enableHiding: false,
				meta: {
					label: "Kind",
					variant: "multiSelect",
					options: kindOptions,
				},
			},
			{
				id: "status",
				accessorKey: "status",
				header: "Status",
				cell: ({ row }) => <StatusBadge status={row.original.status} />,
				enableSorting: false,
				enableColumnFilter: true,
				meta: {
					label: "Status",
					variant: "multiSelect",
					options: STATUS_OPTIONS,
				},
			},
			{
				id: "startedAt",
				accessorKey: "startedAt",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Started" />
				),
				cell: ({ row }) => (
					<span title={new Date(row.original.startedAt).toLocaleString()}>
						{formatRelative(row.original.startedAt)}
					</span>
				),
			},
			{
				id: "finishedAt",
				accessorKey: "finishedAt",
				header: ({ column }) => (
					<DataTableColumnHeader column={column} title="Finished" />
				),
				cell: ({ row }) =>
					row.original.finishedAt ? (
						<span title={new Date(row.original.finishedAt).toLocaleString()}>
							{formatRelative(row.original.finishedAt)}
						</span>
					) : (
						<span className="text-muted-foreground">—</span>
					),
			},
			{
				id: "exitCode",
				accessorKey: "exitCode",
				header: "Exit",
				cell: ({ row }) => (
					<span className="font-mono text-xs tabular-nums">
						{row.original.exitCode ?? "—"}
					</span>
				),
				enableSorting: false,
			},
			{
				id: "user",
				header: "By",
				cell: ({ row }) => row.original.user?.username ?? "—",
				enableSorting: false,
				enableHiding: false,
			},
		],
		[kindOptions],
	);

	const { table } = useDataTable({
		data: data?.data ?? [],
		columns,
		pageCount: data?.pagination.pageCount ?? -1,
		initialState: {
			pagination: { pageIndex: page - 1, pageSize: perPage },
			sorting: DEFAULT_SORTING,
		},
		getRowId: (row) => row.id,
		clearOnDefault: true,
	});

	const filtered = !!kind || !!status;

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-2xl font-semibold tracking-tight">Operations</h1>
				<p className="text-sm text-muted-foreground">
					Audit log of every cluster operation.
				</p>
			</div>

			{isError ? (
				<QueryError
					title="Couldn't load operations"
					error={error}
					onRetry={() => void refetch()}
					retrying={isFetching}
				/>
			) : (
				<DataTable
					table={table}
					isLoading={isLoading}
					loadingRowCount={10}
					loadingFilterCount={2}
					cellWidths={["10rem", "6rem", "7rem", "7rem", "4rem", "6rem"]}
					// Positioned ancestor for the kind cell's stretched link.
					rowClassName="relative cursor-pointer"
					emptyState={
						filtered ? (
							<EmptyState
								icon={Activity}
								title="No operations match these filters"
								description="Try widening the kind or status filter."
								action={
									<Button
										variant="outline"
										size="sm"
										onClick={() => table.resetColumnFilters()}
									>
										Clear filters
									</Button>
								}
							/>
						) : (
							<EmptyState
								icon={Activity}
								title="No operations yet"
								description="Publishing a release, rendering the load balancer or rolling a worker all record an operation here."
							/>
						)
					}
				>
					<DataTableToolbar table={table} />
				</DataTable>
			)}
		</div>
	);
}
